import { AssetSubClassCode } from '@api/api/entity-constants';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { About } from '@terminal/modules/about/About';
import { OverviewEventFeed } from '@terminal/modules/overview-event-feed/OverviewEventFeed';
import { OverviewFinancials } from '@terminal/modules/overview-financials/OverviewFinancials';
import { ThemeKnowledgeGraph } from '@terminal/modules/overview-knowledge-graph/components/theme-knowledge-graph/ThemeKnowledgeGraph';
import { OverviewValuation } from '@terminal/modules/overview-valuation-metrics/OverviewValuationMetrics';
import { useEarningsInsightsByEntities } from '@terminal/react-query-hooks/earnings/useEarningsInsightsByEntities/useEarningsInsightsByEntities';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import clsx from 'clsx';

import { BrandsAndProductsSection } from '../knowledge-graph-widget/components/brands-and-products-section/BrandsAndProductsSection';
import { CompetitorsSection } from '../knowledge-graph-widget/components/competitors-tab/tabs/competitor-detail/components/competitors-section/CompetitorsSection';
import { EarningsInsightSection } from '../knowledge-graph-widget/components/earnings-insight-section/EarningsInsightSection';
import { GeographicExposureSection } from '../knowledge-graph-widget/components/geographic-exposure-tab/geographic-exposure-section/geographic-exposure-section/GeographicExposureSection';
import { UpgradeToUnlock } from './components/upgrade-to-unlock/UpgradeToUnlock';
import styles from './LegacyOverview.module.scss';

import { OverviewChart, OverviewProps } from './Overview';

/** @deprecated */
export const LegacyOverview = ({
  entity,
  isSeriesLoading = false,
}: OverviewProps) => {
  const isStock = entity.sub_class === AssetSubClassCode.EquityStock;

  const { byEntityTag, isLoading: isEarningsLoading } =
    useEarningsInsightsByEntities(isStock ? [entity.tag] : []);
  const {
    latestEarningsData,
    earningsInsight,
    isPreview = false,
  } = byEntityTag.get(entity.tag) ?? {};

  const isBasicUser = useSubscription(state => state.isBasicUser());

  const displayConnections = isStock;
  const hasEarningsSection = isStock && Boolean(latestEarningsData);

  return (
    <>
      <div className={styles.overviewRoot}>
        <OverviewChart entity={entity} isSeriesLoading={isSeriesLoading} />
        <div className={styles.sectionsGrid}>
          <div
            className={clsx(styles.eventFeedCell, {
              [styles.eventFeedCellSingleRow]: !hasEarningsSection,
              [styles.eventFeedCellNonStock]: !isStock,
            })}
          >
            {isBasicUser ? (
              <div className={styles.upgradeToUnlockWrapper}>
                <UpgradeToUnlock />
              </div>
            ) : (
              <ErrorBoundaryWrapper>
                <OverviewEventFeed
                  entity={entity}
                  source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
                />
              </ErrorBoundaryWrapper>
            )}
          </div>
          {hasEarningsSection && (
            <div className={styles.earningsCell}>
              <ErrorBoundaryWrapper>
                <EarningsInsightSection
                  entity={entity}
                  earningsData={latestEarningsData}
                  earningsInsight={earningsInsight}
                  isLoading={isEarningsLoading}
                  isPreview={isPreview}
                />
              </ErrorBoundaryWrapper>
            </div>
          )}
          <div
            className={clsx(styles.aboutCell, {
              [styles.aboutCellTopRow]: !hasEarningsSection,
              [styles.aboutCellNonStock]: !isStock,
            })}
          >
            <About
              entity={entity}
              variant="secondary"
              showMoreDescription
              limit={600}
            />
          </div>
          {displayConnections && (
            <>
              <div className={clsx(styles.gridCell, styles.themeCell)}>
                <ErrorBoundaryWrapper>
                  <ThemeKnowledgeGraph entityTag={entity.tag} />
                </ErrorBoundaryWrapper>
              </div>
              <div className={clsx(styles.gridCell, styles.competitorsCell)}>
                <ErrorBoundaryWrapper>
                  <CompetitorsSection entityTag={entity.tag} />
                </ErrorBoundaryWrapper>
              </div>
              <div className={clsx(styles.gridCell, styles.geographicCell)}>
                <ErrorBoundaryWrapper>
                  <GeographicExposureSection entityTag={entity.tag} />
                </ErrorBoundaryWrapper>
              </div>
              <div className={clsx(styles.gridCell, styles.brandsCell)}>
                <ErrorBoundaryWrapper>
                  <BrandsAndProductsSection entityTag={entity.tag} />
                </ErrorBoundaryWrapper>
              </div>
            </>
          )}
        </div>
        {isStock && (
          <>
            <OverviewFinancials entity={entity} />
            <OverviewValuation entity={entity} />
          </>
        )}
      </div>
    </>
  );
};
